import React, { useState } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { FormControl, TextField } from '@material-ui/core';
import moment from 'moment-timezone';
import { AppButton } from '../common/components/AppButton';
import { SingleSelectDropDown } from '../common/components/SingleSelectDropDown';
import { AppTheme } from '../styles/AppTheme';

const getHourOptions = (label, timezoneLabel) => Array.from({ length: 24 }, (_, hour) => ({
  key: String(hour),
  value: `${label} (${timezoneLabel}): ${moment({ hour }).format('hh A')}`
}))

export const PlaybackHistoryFilterBar = ({
  viewMode,
  startTs: initialStartTs,
  endTs: initialEndTs,
  startHr: initialStartHr,
  endHr: initialEndHr,
  venueCode: initialVenueCode,
  timezone,
  timezoneLabel,
  inProcess,
  showHourFilters = true,
  buttonLabel = 'Search',
  onFilter
}) => {
  const [startTs, setStartTs] = useState(initialStartTs)
  const [endTs, setEndTs] = useState(initialEndTs)
  const [startHr, setStartHr] = useState(initialStartHr)
  const [endHr, setEndHr] = useState(initialEndHr)
  const [venueCode, setVenueCode] = useState(initialVenueCode)
  const isDefaultView = viewMode === 'ssp-only'

  const formatDate = (timestamp) => {
    const date = moment(Number(timestamp)).tz(timezone)
    return timestamp && date.isValid() ? date.format('YYYY-MM-DD') : ''
  }

  const handleDateChange = (event, onChange) => {
    const date = event.target.value
    onChange(date ? moment.tz(date, 'YYYY-MM-DD', timezone).valueOf() : null)
  }

  const handleSubmit = () => {
    onFilter({ startTs, endTs, startHr, endHr, venueCode })
  }

  const renderDateField = ({ label, value, onChange }) => (
    <div className={css(Styles.containerItems)}>
      <FormControl className={css(Styles.formControl)} margin='normal' fullWidth>
        <TextField
          name={label}
          placeholder={label}
          type='date'
          value={formatDate(value)}
          onChange={(event) => handleDateChange(event, onChange)}
          InputLabelProps={{ shrink: false }}
          InputProps={{
            classes: {
              underline: css(Styles.noUnderline, Styles.inputStyle)
            },
            endAdornment: (
              <img
                alt=''
                className={css(Styles.calendarIcon)}
                src={require('../assets/icons/calendar-blue.svg')}
              />
            ),
            startAdornment: null
          }}
        />
      </FormControl>
    </div>
  )

  const renderHourPicker = ({ label, value, onChange }) => (
    <div className={css(Styles.containerItems, Styles.hourItem)}>
      <SingleSelectDropDown
        onSinglePickerFilter={(event) => onChange(event.target.value)}
        singleSelectPicker={{
          defaultValue: value,
          options: getHourOptions(label, timezoneLabel)
        }}
        inputContainer={Styles.hourSelect}
        textInputStyles={Styles.inputStyle}
      />
    </div>
  )

  return (
    <div className={css(Styles.container)}>
      <form autoComplete='off' className={css(Styles.form)} onSubmit={(event) => event.preventDefault()}>
        <div className={css(Styles.filterContainer)}>
          {renderDateField({
            label: isDefaultView ? 'Start Date' : 'Date',
            value: startTs,
            onChange: setStartTs
          })}
          {isDefaultView && renderDateField({
            label: 'End Date',
            value: endTs,
            onChange: setEndTs
          })}
          {isDefaultView && showHourFilters && renderHourPicker({
            label: 'Start Hour',
            value: startHr,
            onChange: setStartHr
          })}
          {isDefaultView && showHourFilters && renderHourPicker({
            label: 'End Hour',
            value: endHr,
            onChange: setEndHr
          })}
          <div className={css(Styles.containerItems)}>
            <FormControl className={css(Styles.formControl)} margin='normal' fullWidth>
              <TextField
                fullWidth
                placeholder='Screen (eg: DD-886547)'
                value={venueCode || ''}
                onChange={(event) => setVenueCode(event.target.value)}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') handleSubmit()
                }}
                InputProps={{
                  classes: {
                    underline: css(Styles.noUnderline, Styles.inputStyle)
                  }
                }}
              />
            </FormControl>
          </div>
          <div className={css(Styles.actions)}>
            <AppButton
              buttonText={buttonLabel}
              inProcess={inProcess}
              onClick={handleSubmit}
              buttonStyle={Styles.button}
              buttonTextStyle={Styles.buttonTextStyle}
              progressIcon={Styles.progressIcon}
            />
          </div>
        </div>
      </form>
    </div>
  )
}

const Styles = StyleSheet.create({
  calendarIcon: {
    position: 'absolute',
    right: 0,
    background: '#fff',
    pointerEvents: 'none'
  },
  filterContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    gap: 12,
    '@media (max-width: 1024px)': {
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      gap: '8px !important'
    }
  },
  containerItems: {
    flex: '1 1 180px',
    minWidth: 180,
    background: '#fff',
    padding: '0 13px',
    borderRadius: 8,
    height: 45,
    boxSizing: 'border-box'
  },
  formControl: {
    marginTop: 8
  },
  inputStyle: {
    fontSize: 16,
    color: '#575757'
  },
  noUnderline: {
    '::before': {
      borderBottom: 'none'
    },
    '::hover::before': {
      borderBottom: 'none'
    },
    '::after': {
      borderBottom: 'none'
    }
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 8,
    marginTop: 16,
    maxWidth: AppTheme.viewPort
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    maxWidth: AppTheme.viewPort,
    padding: 0,
    width: '100%',
    borderRadius: 8
  },
  hourItem: {
    padding: 0
  },
  hourSelect: {
    width: '100% !important',
    maxWidth: 'none !important',
    minWidth: '180px !important',
    height: '45px !important',
    border: 'none !important',
    borderRadius: '8px !important',
    boxSizing: 'border-box'
  },
  actions: {
    flexShrink: 0,
    '@media (max-width: 767px)': {
      width: '100%'
    }
  },
  button: {
    background: '#ffffff',
    color: AppTheme.primaryColor,
    backgroundColor: AppTheme.secondaryBg,
    display: 'inline-flex'
  },
  buttonTextStyle: {
    fontWeight: 550
  },
  progressIcon: {
    color: '#0008be'
  }
})

export default PlaybackHistoryFilterBar



// WEBPACK FOOTER //
// ./src/components/PlaybackHistoryFilterBar.js